<template>
  <main class="writeoffs-page">
    <div class="writeoffs-page__body" ref="body">
      <div class="writeoffs-page__row">
        <h1 class="writeoffs-page__title">Списание товаров</h1>
        <div class="writeoffs-page__buttons">
          <button
            class="writeoffs-page__buttons-add"
            type="button"
            v-on:click="openWriteOffDialogForm">
            Добавить списание
          </button>
        </div>
      </div>
      <write-off-table
        v-bind:write-off-list="writeOffList"
        v-bind:is-await-request="isAwaitRequest"
        v-on:delete-write-off="onDeleteWriteOff"
        v-on:edit-write-off="onRouteWriteOff" />
      <the-pagination v-model="page" v-bind:page-count="pageCount" />
    </div>
    <side-slide-transition>
      <confirm-side-dialog
        class="writeoffs-page__side-dialog"
        label='Добавить списание'
        v-if="showWriteOffDialogForm"
        v-on:cancel="closeWriteOffDialogForm">
        <form-write-off-products
          v-bind:product-options="products"
          v-bind:input-errors="inputErrors"
          v-bind:initial-date="new Date()"
          v-bind:is-await-request="isAwaitWriteoffRequest"
          v-on:submit="createWriteOff"
          v-on:cancel="closeWriteOffDialogForm"
          v-on:change="clearErrors" />
      </confirm-side-dialog>
    </side-slide-transition>
  </main>
</template>

<script>
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import ThePagination from '@/components/ThePagination.vue';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import notifierMixin from '@/mixins/notifierMixin';
import FormWriteOffProducts from '@/components/Warehouse/FormWriteOffProducts.vue';
import WriteOffTable from '@/components/Warehouse/WriteOffTable.vue';
import { WriteOff } from '@/storage/gateways/WriteOffGateway';

/**
 * @description Страница списка списаний товаров.
 * @type {Vue}
 */
export default {
  name: 'ClinicPanelWarehouseProductWriteOffList',
  components: {
    WriteOffTable,
    FormWriteOffProducts,
    SideSlideTransition,
    ThePagination,
    ConfirmSideDialog,
  },
  created() {
    this.fetchOptions();
    this.fetchWriteOffList();
  },
  data() {
    return {
      writeOffList: [],
      isAwaitRequest: false,
      isAwaitWriteoffRequest: false,
      page: 1,
      pageCount: 0,
      selectedWriteOffId: '',
      selectedWriteOff: new WriteOff(null, []),
      inputErrors: {},
      showWriteOffDialogForm: false,
      isAwaitDownloadRequest: false,
      products: [],
    };
  },
  methods: {
    /**
     * @description Создать списания.
     * @param {WriteOffRequest} request
     */
    createWriteOff(request) {
      this.isAwaitWriteoffRequest = true;
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.closeWriteOffDialogForm();
        this.inputErrors = {};
        this.fetchWriteOffList();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$writeOffGateway
        .create(request.toQuery())
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitWriteoffRequest = false; });
    },
    /**
     * @description Очистить ошибки.
     */
    clearErrors() {
      this.inputErrors = {};
    },
    /**
     * @description Закрыть диалоговое окно формы списания.
     * @returns {void}
     */
    closeWriteOffDialogForm() {
      this.selectedWriteOffId = '';
      this.selectedWriteOff = new WriteOff(null, []);
      this.showWriteOffDialogForm = false;
    },
    /**
     * @description Открыть диалоговое окно формы списания.
     * @returns {void}
     */
    openWriteOffDialogForm() {
      this.fetchOptions();
      this.showWriteOffDialogForm = true;
      this.inputErrors = {};
    },
    /**
     * @description Получить список списаний.
     * @returns {Promise<void>}
     */
    async fetchWriteOffList() {
      const onSuccess = ({ writeOffs, pageCount }) => {
        this.writeOffList = writeOffs;
        this.pageCount = pageCount;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.isAwaitRequest = true;
      const either = await this.$writeOffGateway.getAll(this.page);
      either.fold(onFail, onSuccess);
      this.isAwaitRequest = false;
    },
    /**
     * @description Получить опции.
     * @param {string|null} selectedWriteOffId
     * @returns {Promise}
     */
    fetchOptions(selectedWriteOffId = null) {
      const onSuccess = ({ products }) => {
        this.products = products;
      };
      const onFail = (error) => {
        this.selectedWriteOffId = '';
        this.notifyFail(error.getTitle());
      };
      return this
        .$writeOffGateway
        .viewOptions(selectedWriteOffId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать событие удаления списания.
     * @param {number} id Идентификатор списания.
     * @returns {void}
     */
    async onDeleteWriteOff(id) {
      const dialog = await this
        .showRemoveDialog('Вы действительно хотите безвозвратно удалить списание товаров?');
      if (dialog.result !== DialogResult.DELETE) {
        return;
      }
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.fetchWriteOffList();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$writeOffGateway
        .delete(id)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Переход на страницу списания.
     * @param {string} id
     * @returns {Promise<void>}
     */
    async onRouteWriteOff(id) {
      await this.$router.push({
        name: 'ClinicPanelWarehouseProductWriteOff',
        params: {
          writeOffId: id,
        },
      });
    },
  },
  mixins: [
    notifierMixin,
    builtInDialogMixin,
  ],
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.writeoffs {
  &-page {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    &__filters {
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 20px;
      &-label {
        color: gray;
        font-size: 12px;
      }
      &-additional {
        display: flex;
        gap:10px;
        margin-top: 20px;
      }
    }
    &__categories {
      width: 280px;
      &__caption {
        font-size: 14px;
        line-height: 17px;
        font-weight: 700;
        border-bottom: 2px solid #0489e0;
        padding: 10px;
      }
      &__container {
        display: flex;
        flex-direction: column;
        gap: 15px;
        width: 280px;
        background: #f2f7fa;
        padding: 20px 10px;
      }
    }
    &__buttons {
      display: grid;
      grid-template-columns: 1fr;
      max-width: 225px;
      width: 100%;
      column-gap: 10px;
      align-items: center;
      &-filter {
        margin-top: 25px;
        display: flex;
        align-items: center;
        gap: 5px;
        max-width: 175px;
        width: 100%;
        color: #1F85C8;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        >img {
          width: 15px;
          height: 15px;
        }
      }
      &-add {
        @include buttons.btn-blue(100%, 48px, 16px);
      }

      &-export {
        @include buttons.btn-transparent(100%, 48px, 16px);

        &:before {
          content: "";
          background-image: url("@/assets/img/download-file-blue.svg");
          background-repeat: no-repeat;
          background-size: contain;
          width: 20px;
          height: 20px;
          margin: 0 8px 0 0;
        }

        &:disabled {
          color: var(--unfocus-color);
          border-color: var(--unfocus-color);
          background-color: transparent;
        }
      }

      &-delete {
        @include buttons.btn-blue(50%, 48px, 16px);
        width: 100%;
      }

      &-delete:disabled {
        background-color: var(--unfocus-color);
      }
    }

    &__body {
      padding: 38px 20px 48px 23px;
      width: 100%;
      overflow-y: scroll;
    }

    &__row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }

    &__search {
      max-width: 400px;
    }
    &__side-dialog {
      .side-dialog {
        padding-bottom: 250px;
      }
    }
  }
}

@media screen and (max-width: 960px) {
  .writeoffs {
    &-page {
      flex-direction: column;

      &__row {
        flex-direction: column;
        gap: 15px;
      }

      &__search {
        max-width: 100%;
      }
    }
  }
}

@media screen and (max-width: 640px) {
  .writeoffs {
    &-page {
      &__body {
        padding: 38px 10px 140px;
        width: 100%;
        overflow-y: scroll;
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .writeoffs-page__buttons-export {
    display: none;
  }
}
</style>
